import useCapabilities, { PERMISSION } from "@/hooks/useCapabilities";
import { isActivityType } from "@/pages/activities/activity-types/_utils";
import { getEventKind, getEventTypeName, type EventWithType } from "./utils";

const isUniversitySeminarEvent = (event: EventWithType | undefined): boolean =>
  isActivityType(getEventTypeName(event), "education");

type UseCanCancelRegistrationOptions = {
  canAccessActivityManagement: boolean;
};

/**
 * Whether the user has permission to see the Cancel Registration action.
 * Conditional availability (e.g. PAT registration still open) is separate —
 * see {@link isCancelRegistrationPerformable} in `./utils`.
 *
 * Seminar / education cancel uses the same activity-management gate as transfer.
 * Backend cancel for non-PAT events only requires manage_event_registrations?
 * (manageEvents + visibility); it does not require manageEducationActivities.
 */
export default function useCanCancelRegistration(
  event: EventWithType | undefined,
  { canAccessActivityManagement }: UseCanCancelRegistrationOptions
): boolean {
  const { hasPermission } = useCapabilities();

  switch (getEventKind(event)) {
    case "pat":
      return hasPermission(PERMISSION.editPAT);
    case "seminar":
    case "education":
      return (
        canAccessActivityManagement ||
        (hasPermission(PERMISSION.manageOwnUniversity) && isUniversitySeminarEvent(event))
      );
    default:
      return hasPermission(PERMISSION.editPAT);
  }
}
